import { z } from 'zod';
import { Priority } from '../types/task';
import { TASK_REPEAT_RULES } from '../scheduling/task-recurrence';
import { httpsUrlSchema } from './https-url';

/**
 * GHE #683 — guarda Zod de los tres preajustes de repetición.
 *
 * 🚨 Se construye A PARTIR de `TASK_REPEAT_RULES`, no repitiendo los literales. La columna de la
 * base es `NVARCHAR` (convención de la casa: un enum de SQL Server es una migración por cada
 * valor), así que esta validación es la ÚNICA puerta que decide qué llega a esa columna. Si la
 * lista se escribiera dos veces, añadir un cuarto preajuste dejaría una de las dos atrás y el
 * síntoma sería un 400 incomprensible, o peor, una regla que se guarda y que el motor de fechas
 * no sabe interpretar.
 */
export const TaskRepeatRuleEnum = z.enum(TASK_REPEAT_RULES);

/**
 * Zod guard for the shared {@link Priority} const enum. Exported so other schema
 * files (e.g. my-plan.schema) mirror priority from ONE source instead of
 * re-declaring the literal list.
 */
export const PriorityEnum = z.enum([
  Priority.URGENT,
  Priority.HIGH,
  Priority.NORMAL,
  Priority.LOW,
  Priority.NONE,
]);

/**
 * A link to the work item this task mirrors elsewhere (#738) — Azure DevOps on import, anything
 * `https:` by hand.
 *
 * 🚨 THIS IS AN INJECTION SURFACE, and the obvious validator does not close it. The rule and
 * the reasoning now live in `https-url.ts` (extracted for #703, when the custom-field URL type
 * became a second surface storing a user-supplied link) — read `isHttpsUrl`'s comment before
 * touching anything here. Behaviour is unchanged: `https:` only, host required, trimmed,
 * capped at 2048, same `task.externalUrl.mustBeHttps` message key.
 */
export const ExternalUrlSchema = httpsUrlSchema('task.externalUrl.mustBeHttps');

/**
 * Validation schema for creating a new task.
 * Used on POST /api/v1/tasks.
 * @example
 * const input = CreateTaskSchema.parse({
 *   title: 'Fix login bug',
 *   listId: 'list-uuid',
 *   statusId: 'status-uuid',
 * });
 */
export const CreateTaskSchema = z
  .object({
    /** Task title (required, 1-500 characters) */
    title: z.string().min(1).max(500),
    /** Optional task description */
    description: z.string().optional(),
    /** Priority level (defaults to NORMAL) */
    priority: PriorityEnum.optional(),
    /** ID of the list to create task in (required) */
    listId: z.string().uuid(),
    /** ID of the initial status (required) */
    statusId: z.string().uuid(),
    /** ID of parent task (for subtasks) */
    parentId: z.string().uuid().optional(),
    /** Due date in ISO 8601 format */
    dueDate: z.string().datetime().optional(),
    /** Start date in ISO 8601 format */
    startDate: z.string().datetime().optional(),
    /** Time estimate in minutes */
    estimate: z.number().int().nonnegative().optional(),
    /** Client this task is for — MANDATORY at creation (owner decision 2026-07-05). Legacy tasks may still have none. */
    clientId: z.string().uuid(),
    /**
     * Task type (ADR 0032, #398). Optional — a task need not have one, and an untyped
     * task is not implicitly a Requirement. Governed by FIELD_POLICY `manage`.
     */
    taskTypeId: z.string().uuid().optional(),
    /**
     * GHE #1291 — el área de conocimiento que la tarea necesita. Ausente ⇒ se hereda el defecto de
     * la LISTA; presente ⇒ manda lo que diga quien crea (incluido `null`, que es «ninguna» a
     * propósito y no «no me has dicho nada»).
     */
    specialtyId: z.string().uuid().nullable().optional(),
    /**
     * Per-task override of the type's validation-plan requirement (ADR 0032 D2).
     *
     * NULLABLE ON PURPOSE, and the nullability is the point: `null`/omitted means
     * "inherit the type", `true`/`false` are deliberate human decisions. Governed by
     * FIELD_POLICY `manage` — `false` exempts the task from the #25 completion gate,
     * so it must never be reachable from a member-tier write.
     */
    requiresValidation: z.boolean().nullable().optional(),
    /** #738 — link to the mirrored work item. `https:` only; see {@link ExternalUrlSchema}. */
    externalUrl: ExternalUrlSchema.optional(),
    /**
     * GHE #683 — cada cuánto se repite.
     *
     * GHE #1440 — NULLABLE también al crear, porque ahora «ausente» y «no se repite» son dos
     * respuestas distintas: ausente ⇒ lo que diga el tipo (`TaskType.defaultRepeatRule`), `null`
     * ⇒ esta tarea no se repite aunque su tipo sí. Sin el `null`, quien crea un «Recurso» no
     * tendría forma de decir «éste no».
     *
     * El valor viaja como cadena y lo acota este enum, no la base: la columna es `NVARCHAR` por
     * la convención de la casa (un enum de SQL Server es una migración por cada valor nuevo), así
     * que ESTE es el único sitio donde se decide qué es admisible.
     */
    repeatRule: TaskRepeatRuleEnum.nullable().optional(),
    /** Fin de la serie, inclusive. Ausente ⇒ indefinida. */
    repeatUntil: z.string().datetime().optional(),
    /**
     * ADR 0037 (#1085) — the previous development this task came out of.
     *
     * ⚠️ `previousDeveloperId` is only meaningful alongside a `relatedTaskId`, and that pairing
     * is deliberately NOT enforced here. A partial update may send only one of the two, so only
     * `TaskService` — which loads the stored row — can judge the combination that WOULD RESULT.
     * A `.refine()` here would wave through an update that omitted the other field. Same
     * reasoning as the task-type exclusion (ADR 0034 D2).
     */
    relatedTaskId: z.string().uuid().optional(),
    previousDeveloperId: z.string().uuid().optional(),
    /**
     * Valores de campos personalizados, EN LA MISMA LLAMADA que crea la tarea (#694).
     *
     * 🚨 Por qué aquí y no en una segunda llamada: el diálogo de asignar ya enseñó esa lección
     * (#1250). `useCreateTask` creaba la tarea y asignaba DESPUÉS, en una llamada aparte cuyo
     * error se tragaba, así que un fallo dejaba la tarea creada a medias con un aviso encima.
     * Un formulario de admisión con un campo que falle al guardar dejaría una petición a medias
     * que nadie sabe que lo está — el mismo defecto con otra cara.
     *
     * La forma del valor es la misma que acepta `bulkSetFieldValues`, para que la validación por
     * tipo sea LITERALMENTE la misma función y no una segunda copia que envejezca aparte.
     */
    customFieldValues: z
      .array(
        z.object({
          fieldId: z.string().uuid(),
          value: z.union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.null()]),
        }),
      )
      .max(50)
      .optional(),
  })
  // #274 — a due date earlier than the start date silently flowed through into Gantt
  // bars, workload and reports. Mirrors calendar.schema.ts's `dateRange.endBeforeStart`
  // (CreateVacationRequestSchema) exactly — same stable message key, same ISO-string
  // lexicographic comparison. Only fires when BOTH dates are present in this request;
  // TaskDetailPanel additionally binds `min`/`max` on the two date inputs so the native
  // picker can't offer an invalid combination in the first place.
  .refine((v) => v.dueDate == null || v.startDate == null || v.dueDate >= v.startDate, {
    message: 'dateRange.endBeforeStart',
    path: ['dueDate'],
  });

/** Type inferred from CreateTaskSchema */
export type CreateTaskInput = z.infer<typeof CreateTaskSchema>;

/**
 * Validation schema for updating an existing task.
 * Used on PATCH /api/v1/tasks/{id}.
 * Includes version for optimistic locking.
 *
 * Field-level policy (ADR 0018): every data field below is additionally governed by
 * the shared `FIELD_POLICY` (`policy/field-policy.ts`) at the service chokepoints —
 * schema validity does NOT imply the caller may edit the field. `version` is
 * optimistic-locking control metadata, exempt from field policy.
 * @example
 * const input = UpdateTaskSchema.parse({
 *   title: 'New title',
 *   version: 2,
 * });
 */
export const UpdateTaskSchema = z
  .object({
    /** Updated task title */
    title: z.string().min(1).max(500).optional(),
    /** Updated task description (can be null to clear) */
    description: z.string().nullable().optional(),
    /** Updated priority level */
    priority: PriorityEnum.optional(),
    /** Updated list ID (change which list task belongs to) */
    listId: z.string().uuid().optional(),
    /** Updated status ID (change column) */
    statusId: z.string().uuid().optional(),
    /** Updated parent task ID (change subtask parent) */
    parentId: z.string().uuid().nullable().optional(),
    /** Updated due date (can be null to clear) */
    dueDate: z.string().datetime().nullable().optional(),
    /** Updated start date (can be null to clear) */
    startDate: z.string().datetime().nullable().optional(),
    /** Updated estimate in minutes (can be null to clear) */
    estimate: z.number().int().nonnegative().nullable().optional(),
    /**
     * Real time worked in minutes (can be null to clear). Governed by FIELD_POLICY
     * `write` + `requiresAssignment` — members may set it only on their own tasks.
     */
    actualMinutes: z.number().int().nonnegative().nullable().optional(),
    /**
     * Updated client ID (switch to a different client). NOT nullable — a task cannot be
     * detached from its client once created (#95 B1: CreateTaskSchema.clientId is
     * mandatory, so clearing it here would silently drop the client
     * TASK_FINISHED_CLIENT email, which keys off `effectiveClientId`). Omit the field to
     * leave the client unchanged.
     *
     * ⚠️ This used to cite the #25 validation-plan gate as a second reason. ADR 0032
     * retired that link — the gate no longer reads `clientId` at all — so the email is
     * now the whole justification. The rule is unchanged; only its basis narrowed.
     */
    clientId: z.string().uuid().optional(),
    /**
     * Updated task type (ADR 0032, #398). Nullable — a task can be untyped again, which
     * simply drops it to `requiresValidation ?? false`. Governed by FIELD_POLICY `manage`.
     */
    taskTypeId: z.string().uuid().nullable().optional(),
    /** GHE #1291 — reasignar el área. `null` la quita. Gobernado por `FIELD_POLICY`. */
    specialtyId: z.string().uuid().nullable().optional(),
    /**
     * Per-task override of the validation-plan requirement (ADR 0032 D2). Send `null` to
     * go back to inheriting the type; `true`/`false` to decide explicitly. Governed by
     * FIELD_POLICY `manage` — see CreateTaskSchema for why `false` cannot be write-tier.
     */
    requiresValidation: z.boolean().nullable().optional(),
    /** Mark/unmark as a milestone (Phase 12). Governed by FIELD_POLICY `manage`. */
    isMilestone: z.boolean().optional(),
    /**
     * #738 — link to the mirrored work item. `https:` only; see {@link ExternalUrlSchema}.
     *
     * NULLABLE, and that is the whole point of the distinction here: `null` means "remove the
     * link", omitted means "leave it alone". Collapsing the two would make the field
     * unclearable once set — the import fills it automatically, so a wrong link would be
     * permanent. Governed by FIELD_POLICY `manage`.
     */
    externalUrl: ExternalUrlSchema.nullable().optional(),
    /**
     * GHE #683 — cada cuánto se repite.
     *
     * NULLABLE, y la distinción es justo el mecanismo de parar una serie: `null` es «deja de
     * repetirse» y omitido es «no toques esto». Si se colapsaran, una serie indefinida no se
     * podría apagar nunca desde la ficha — y la decisión del propietario de permitir series sin
     * fecha de fin DEPENDE de que apagarla sea trivial.
     */
    repeatRule: TaskRepeatRuleEnum.nullable().optional(),
    /** Fin de la serie, inclusive. `null` = quitar el fin; omitido = no tocarlo. */
    repeatUntil: z.string().datetime().nullable().optional(),
    /**
     * ADR 0037 (#1085). Nullable here where the create schema is not: clearing the link is a
     * real gesture ("Quitar"), and `null` is how the wire says it. Clearing `relatedTaskId`
     * also clears `previousDeveloperId` — a previous developer with no previous development is
     * a claim about nothing (ADR 0037 D6) — and that cascade lives in `TaskService`, for the
     * partial-update reason above.
     */
    relatedTaskId: z.string().uuid().nullable().optional(),
    previousDeveloperId: z.string().uuid().nullable().optional(),
    /** Current version (required for optimistic locking) */
    version: z.number().int().positive(),
  })
  // #274 — same `dateRange.endBeforeStart` guard as CreateTaskSchema above. A PATCH only
  // ever edits ONE of the two date fields at a time in the current UI (TaskDetailPanel
  // saves each field independently on its own change/blur), so this only fires for a
  // caller that sends both together in one request — but it's the correct
  // defense-in-depth backstop regardless of which caller does that.
  .refine((v) => v.dueDate == null || v.startDate == null || v.dueDate >= v.startDate, {
    message: 'dateRange.endBeforeStart',
    path: ['dueDate'],
  });

/** Type inferred from UpdateTaskSchema */
export type UpdateTaskInput = z.infer<typeof UpdateTaskSchema>;

/**
 * Body for POST /api/v1/tasks/{id}/complete (ADR 0018 slice 3) — the atomic
 * done-transition action: sets a DONE-category status + `completedAt` +
 * `actualMinutes`, and optionally applies the previewed pull-forward to the
 * assignee's next task (same space, TOCTOU-guarded via that task's version).
 * Never a raw PATCH: `completedAt` stays un-PATCHable.
 *
 * Field policy applies as if this were a `statusId` + `actualMinutes` edit:
 * members may complete only tasks assigned to them.
 * @example
 * const input = CompleteTaskSchema.parse({ version: 3, actualMinutes: 480, applyPullForward: true });
 */
export const CompleteTaskSchema = z.object({
  /** Current version of the task being completed (optimistic locking) */
  version: z.number().int().positive(),
  /**
   * Target DONE-category status chosen by the caller (panel status picker, board
   * column). Must belong to the task's list AND have category DONE. Omitted (Gantt
   * quick-done) ⇒ the server picks the list's first DONE-category status by position.
   */
  statusId: z.string().uuid().optional(),
  /** Real time worked in minutes; null/omitted leaves the field unchanged */
  actualMinutes: z.number().int().nonnegative().nullable().optional(),
  /** Apply the pull-forward candidate from the completion preview (default false = Skip) */
  applyPullForward: z.boolean().optional().default(false),
  /**
   * The previewed candidate being confirmed (required when applyPullForward=true):
   * the server recomputes the candidate and 409s if it no longer matches this
   * task+version — the user must never confirm a move they weren't shown.
   */
  pullForwardTaskId: z.string().uuid().optional(),
  pullForwardVersion: z.number().int().positive().optional(),
});

/** Type inferred from CompleteTaskSchema */
export type CompleteTaskInput = z.infer<typeof CompleteTaskSchema>;

/**
 * Body for POST /api/v1/tasks/{id}/confirm-deadline-missed (ADR 0018 slice 4).
 * Confirms a computed-overdue task as an acknowledged miss: stamps
 * `deadlineMissedAt`, notifies the space managers and the client's JP/RT
 * (ADR 0013 recipients; in-app now, email rides Batch C). No reason field
 * (owner decision 2026-07-10). Server rejects with 400 when the task is not
 * currently overdue and with 409 on version drift or an already-confirmed miss.
 */
export const ConfirmDeadlineMissedSchema = z.object({
  /** Current version of the task (optimistic locking) */
  version: z.number().int().positive(),
});

/** Type inferred from ConfirmDeadlineMissedSchema */
export type ConfirmDeadlineMissedInput = z.infer<typeof ConfirmDeadlineMissedSchema>;

/**
 * Validation schema for updating task position.
 * Used on PATCH /api/v1/tasks/{id}/position.
 * Supports drag-and-drop within same list or to different status.
 * @example
 * const input = UpdateTaskPositionSchema.parse({
 *   position: 5,
 *   statusId: 'new-status-uuid',
 * });
 */
export const UpdateTaskPositionSchema = z.object({
  /** New position in the list (or column) */
  position: z.number().int().nonnegative(),
  /** Optional: new status (column) ID if moving between columns */
  statusId: z.string().uuid().optional(),
});

/** Type inferred from UpdateTaskPositionSchema */
export type UpdateTaskPositionInput = z.infer<typeof UpdateTaskPositionSchema>;

/**
 * Common pagination parameters for all list endpoints.
 * Supports cursor-based pagination (no offset).
 * @example
 * const input = PaginationSchema.parse({
 *   cursor: undefined,
 *   limit: 50,
 * });
 */
export const PaginationSchema = z.object({
  /** Opaque cursor from previous response's meta.nextCursor */
  cursor: z.string().optional(),
  /** Items per page (1-100, default 50) */
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

/** Type inferred from PaginationSchema */
export type PaginationInput = z.infer<typeof PaginationSchema>;

// ─── Task Filters (ADR 0011, Phase 6a) ───
// Wire format: query-string params (every value arrives as a single string), so
// multi-value filters are CSV-encoded and coerced here. This schema is the ONE
// shared contract for the filter bar (web), the task endpoints (api), and
// SavedView.filters payloads — extend it in one place only.

/** CSV of UUIDs ("a,b,c") → validated string[]. Empty/blank entries rejected. */
const csvUuids = z
  .string()
  .transform((s) => s.split(',').map((v) => v.trim()))
  .pipe(z.array(z.string().uuid()).min(1).max(50));

/** CSV of priorities ("HIGH,URGENT") → validated Priority[]. */
const csvPriorities = z
  .string()
  .transform((s) => s.split(',').map((v) => v.trim()))
  .pipe(z.array(PriorityEnum).min(1).max(5));

/** Query-string boolean: only the literal "true" enables the flag. */
const queryFlag = z.enum(['true', 'false']).transform((v) => v === 'true');

/**
 * A single custom-field condition. v1 matching is substring (`contains`) on the
 * JSON-encoded stored value — equality for scalars, "includes" for text (ADR 0011 D1).
 */
export const CustomFieldFilterSchema = z.object({
  /** CustomFieldDefinition id */
  fieldId: z.string().uuid(),
  /** Raw value to match against the JSON-encoded stored value */
  value: z.string().min(1).max(500),
});

/** Type inferred from CustomFieldFilterSchema */
export type CustomFieldFilter = z.infer<typeof CustomFieldFilterSchema>;

/**
 * Filters accepted by every task-fetch path (list / board / gantt).
 * All fields optional; conditions are ANDed together. Scope-safety is enforced
 * server-side (accessibleSpaceWhere) regardless of what is requested here.
 * @example
 * const filters = TaskFilterSchema.parse({
 *   q: 'login',
 *   priorities: 'URGENT,HIGH',
 *   assigneeIds: '3f8e...,9c1d...',
 *   dueTo: '2026-07-31T00:00:00Z',
 * });
 */
export const TaskFilterSchema = z.object({
  /** Free text — matches title OR description (case-insensitive on SQL Server) */
  q: z.string().trim().min(1).max(200).optional(),
  /** CSV of status ids */
  statusIds: csvUuids.optional(),
  /**
   * GHE #888 — exclude DONE-category tasks from the list.
   *
   * A CATEGORY filter, not a status-id one, deliberately: the client would otherwise have to
   * resolve "which of this list's statuses are DONE" before it could ask, which means waiting
   * on a second query and trading the payload win for a slower first paint. The server already
   * knows the category, so it answers directly.
   */
  // ⚠️ The transform maps `undefined -> undefined`, NOT `undefined -> false`. A bare
  // `.optional().transform(v => v === 'true')` yields a REQUIRED boolean in the output type,
  // which broke every existing caller of this shared filter shape (the report service's own
  // query reuses it). Optional in, optional out.
  excludeDone: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
  /** CSV of priorities (URGENT|HIGH|NORMAL|LOW|NONE) */
  priorities: csvPriorities.optional(),
  /** CSV of assignee user ids — tasks assigned to ANY of them */
  assigneeIds: csvUuids.optional(),
  /** "true" → tasks with NO assignees (ORed with assigneeIds when both present) */
  unassigned: queryFlag.optional(),
  /** Due date range (ISO 8601, inclusive) */
  dueFrom: z.string().datetime().optional(),
  dueTo: z.string().datetime().optional(),
  /** Start date range (ISO 8601, inclusive) */
  startFrom: z.string().datetime().optional(),
  startTo: z.string().datetime().optional(),
  /** Estimate range in minutes (inclusive) */
  estimateMin: z.coerce.number().int().nonnegative().optional(),
  estimateMax: z.coerce.number().int().nonnegative().optional(),
  /** Creator user id */
  createdById: z.string().uuid().optional(),
  /** CSV of client ids — tasks tagged with ANY of them */
  clientIds: csvUuids.optional(),
  /** "true" → tasks with NO client tag (ORed with clientIds when both present) */
  noClient: queryFlag.optional(),
  /** JSON-encoded CustomFieldFilter[] (query-string safe); max 10 conditions */
  customFields: z
    .string()
    .transform((s, ctx) => {
      try {
        return JSON.parse(s) as unknown;
      } catch {
        // Stable message KEY, not prose (Batch B7, docs/phase9/batch-b-i18n.md §2 Pattern A).
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'task.filters.customFieldsInvalidJson',
        });
        return z.NEVER;
      }
    })
    .pipe(z.array(CustomFieldFilterSchema).min(1).max(10))
    .optional(),
});

/** Type inferred from TaskFilterSchema (post-coercion: arrays, booleans, numbers) */
export type TaskFilterInput = z.infer<typeof TaskFilterSchema>;
